/**
 * Mural de novidades do dashboard.
 *
 * Esta lista e escrita para quem usa o sistema, nao para quem o desenvolve: cada
 * item conta o que mudou na tela, sem nome de arquivo, de rota ou de componente.
 * Ao publicar uma versao nova, acrescente um bloco no topo do array (a ordem do
 * array e a ordem da tela) e escreva a descricao pensando na secretaria e nos
 * professores, nao no time de tecnologia.
 */

/** Cor e rotulo da etiqueta de cada item, definidos em `components/Novidades`. */
export type TipoNovidade = 'novidade' | 'melhoria' | 'correcao' | 'seguranca';

/** Quem ve o item no mural. Um item sem publico definido aparece para todo mundo. */
export type PublicoNovidade = 'ADMIN' | 'PROFESSOR';

export type ItemNovidade = {
	id: string;
	titulo: string;
	descricao: string;
	tipo: TipoNovidade;
	publico?: PublicoNovidade[];
};

export type VersaoNovidades = {
	/** Titulo da entrega, do jeito que aparece no mural. */
	versao: string;
	/** Data de publicacao em ISO (`AAAA-MM-DD`), usada para ordenar e marcar o que ainda nao foi lido. */
	data: string;
	/** Uma frase resumindo a entrega, lida antes dos itens. */
	resumo: string;
	itens: ItemNovidade[];
};

export const NOVIDADES: VersaoNovidades[] = [
	{
		versao: 'Cartazes inteiros no mural',
		data: '2026-10-06',
		resumo: 'O mural de informativos passou a mostrar cada cartaz inteiro, sem cortar as pontas, em qualquer formato de arte.',
		itens: [
			{
				id: '2026-10-cartaz-inteiro',
				tipo: 'correcao',
				titulo: 'O mural não corta mais os cartazes',
				descricao:
					'Um cartaz largo, como o de feriados com uma coluna por idioma, perdia as colunas das pontas no computador e quase tudo no celular. Agora a arte aparece inteira em qualquer formato, e o espaço que sobra ao redor é preenchido com as cores da própria arte. O tipo e o título saíram de cima do cartaz e ficam numa faixa logo abaixo, junto das setas e dos indicadores, sem cobrir nada do que está impresso.'
			},
			{
				id: '2026-10-tamanho-do-cartaz',
				tipo: 'melhoria',
				publico: [ 'ADMIN' ],
				titulo: 'O cadastro de informativo mostra a arte como ela vai aparecer',
				descricao:
					'A prévia do cadastro e os cartões da tela de Informativos mostram a arte inteira, do mesmo jeito que o mural. Embaixo do campo Imagem há uma orientação de formato: qualquer arte serve, e uma larga, como 1920 × 400 pixels, é a que melhor ocupa o mural no computador. Também deixou de aparecer o aviso de imagem pesada quando você abre a janela para trocar a arte e desiste.'
			}
		]
	},
	{
		versao: 'Aulas mais fáceis de ler',
		data: '2026-10-04',
		resumo: 'O status de cada aula aparece por extenso e com cor própria, e a tela de aulas explica o que fazer quando ainda não há nada para mostrar.',
		itens: [
			{
				id: '2026-10-status-aula',
				tipo: 'melhoria',
				titulo: 'Status da aula por extenso e com cor própria',
				descricao:
					'As listas de aulas e o gráfico de aulas por status mostram Agendada, Pendente, Iniciada, Concluída e Cancelada, em vez das palavras em maiúsculas e sem acento. Cada status ganhou a sua cor: antes, tudo o que não era concluído aparecia em laranja, inclusive a aula cancelada.'
			},
			{
				id: '2026-10-tela-aulas',
				tipo: 'melhoria',
				titulo: 'A tela de aulas orienta antes de listar',
				descricao:
					'Ao abrir Aulas, a tela pede para escolher um professor em vez de mostrar uma tabela vazia. Se o professor escolhido ainda não tem aula, ela diz isso e oferece o botão Nova aula. A coluna Action, que ficava sempre em branco, saiu da tabela.',
				publico: [ 'ADMIN' ]
			}
		]
	},
	{
		versao: 'Cadastro de estrangeiros',
		data: '2026-08-31',
		resumo: 'Alunos e professores de fora do Brasil agora se cadastram com passaporte ou com o documento do país deles, sem precisar de CPF.',
		itens: [
			{
				id: '2026-08-documento-estrangeiro',
				tipo: 'novidade',
				titulo: 'Passaporte e outros documentos no cadastro',
				descricao:
					'A ficha de aluno e a de professor passaram a ter o campo "Tipo de documento", com as opções CPF, Passaporte e Outro documento. Escolhendo passaporte ou outro documento, o número é aceito no formato do país de origem, sem a exigência dos 11 dígitos do CPF. Quem tem CPF continua sendo cadastrado exatamente como antes.'
			},
			{
				id: '2026-08-documento-listagem',
				tipo: 'melhoria',
				titulo: 'A listagem mostra que documento é',
				descricao:
					'A coluna Cpf das listas de alunos e de professores virou Documento e, quando o cadastro não é por CPF, mostra embaixo do número de que documento se trata.'
			}
		]
	},
	{
		versao: 'Fim de agosto de 2026',
		data: '2026-08-30',
		resumo:
			'Cartazes de informativos para avisar alunos e professores sobre feriados, datas comemorativas e novidades da escola, e a troca de professor direto na ficha do aluno.',
		itens: [
			{
				id: '2026-08-informativos',
				tipo: 'novidade',
				publico: [ 'ADMIN' ],
				titulo: 'Informativos para avisar a escola',
				descricao:
					'Novo item Informativos no menu, para publicar cartazes de feriado, data comemorativa, novidade da escola ou recado geral. Cada cartaz tem uma imagem e um título: o recado vai impresso na arte. Dá para escolher a partir de quando ele entra no ar e até quando fica, o que permite deixar o feriado pronto com antecedência, e a ordem em que os cartazes se revezam é você quem define. A chave Publicado tira do ar sem apagar, para reaproveitar o mesmo cartaz no ano seguinte. A lista mostra a situação de cada um: no mural, agendado, expirado ou despublicado.'
			},
			{
				id: '2026-08-mural-no-painel',
				tipo: 'novidade',
				titulo: 'Os informativos aparecem no seu painel',
				descricao:
					'Os cartazes publicados passam em um banner que abre o painel, acima dos números, e no portal do aluno assim que ele entra. O banner troca de cartaz sozinho a cada poucos segundos e para enquanto você estiver com o mouse em cima, para não puxar o cartaz debaixo de quem está lendo. Clicar abre a arte inteira, grande, que é onde o texto impresso no cartaz fica legível.'
			},
			{
				id: '2026-08-transferencia-de-professor',
				tipo: 'novidade',
				publico: [ 'ADMIN' ],
				titulo: 'Trocar o professor do aluno sem excluir o cadastro',
				descricao:
					'A ficha do aluno ganhou o campo Professor responsável. Ao trocar e salvar, as aulas agendadas e pendentes vão junto para o professor novo; as que já começaram e as que foram concluídas ficam com o professor antigo, para o histórico não mudar de dono. Antes o único jeito de trocar era excluir o aluno e cadastrar de novo, o que apagava a senha dele. A troca é da secretaria: quem dá aula não vê esse campo.'
			}
		]
	},
	{
		versao: 'Agosto de 2026',
		data: '2026-08-03',
		resumo:
			'Quadro para escrever por cima do material durante a aula, formulários que avisam o que falta preencher e uma série de correções nas telas de books, lessons e acesso.',
		itens: [
			{
				id: '2026-08-quadro-interativo',
				tipo: 'novidade',
				titulo: 'Quadro interativo sobre o material',
				descricao:
					'Durante a aula dá para escrever, grifar e apagar por cima do PDF, com caneta, marca-texto e borracha. As marcações ficam guardadas por página e continuam lá ao trocar de lesson, ao dar zoom ou ao reabrir o material no mesmo computador. O modo cursor continua sendo o padrão, então o material só recebe marcações quando você escolher uma ferramenta.'
			},
			{
				id: '2026-08-texto-no-quadro',
				tipo: 'novidade',
				titulo: 'Texto digitado no quadro',
				descricao:
					'A ferramenta de texto escreve com o teclado sobre o material, em três tamanhos e nas mesmas cores da caneta, o que ajuda quando a letra à mão fica pequena demais para a turma acompanhar. O texto continua saindo sem fundo, mas a paleta traz uma fileira de quadradinhos para pôr uma tarja branca, amarela, azul ou verde atrás das letras, que resolve a página escura ou cheia de foto, onde a frase se perde no material. Clique onde o texto deve começar, digite e clique fora para confirmar. Um clique em cima de um texto já escrito o seleciona: daí dá para arrastá-lo para outro lugar, puxar o quadradinho do canto para deixá-lo maior ou menor e trocar a cor pela paleta. O duplo clique abre o texto para correção, e a borracha apaga o texto inteiro de uma vez.'
			},
			{
				id: '2026-08-material-em-um-clique',
				tipo: 'melhoria',
				titulo: 'Material abre em um clique',
				descricao:
					'Clicar no book leva direto para as lessons dele, e clicar na lesson abre o PDF na hora. Antes era preciso passar por uma lista intermediária até chegar no material.'
			},
			{
				id: '2026-08-campos-obrigatorios',
				tipo: 'melhoria',
				titulo: 'Formulários avisam o que falta preencher',
				descricao:
					'Os cadastros de alunos, professores, books, lessons e aulas passaram a marcar com asterisco os campos obrigatórios e a mostrar a mensagem embaixo do campo que ficou errado, em vez de recusar o salvamento sem explicar o motivo. O botão Salvar também responde à tecla Enter.'
			},
			{
				id: '2026-08-filtro-reviews',
				tipo: 'correcao',
				titulo: 'Filtro de reviews das lessons',
				descricao:
					'O filtro de reviews vinha vazio em alguns books, e lessons com o nome fora do padrão sumiam da lista sem deixar como voltar. Agora o filtro olha o nome da lesson, funciona em qualquer idioma e ganhou a opção Todos para limpar a seleção. A busca por texto saiu da tela, já que a lista é curta e o filtro dá conta.'
			},
			{
				id: '2026-08-lista-licoes-estavel',
				tipo: 'correcao',
				titulo: 'Lista de lessons não pisca mais ao salvar',
				descricao:
					'Ao criar ou editar uma lesson, a lista mostrava por um instante todas as lessons, mesmo com o filtro marcado, antes de voltar ao normal. A lista agora respeita o filtro o tempo todo.'
			},
			{
				id: '2026-08-filtro-idioma-books',
				tipo: 'correcao',
				titulo: 'Filtro de idioma dos books preservado',
				descricao:
					'O idioma escolhido no filtro dos books se perdia enquanto a tela carregava e a lista voltava completa. A escolha agora se mantém até você trocá-la.'
			},
			{
				id: '2026-08-reload-mantem-pagina',
				tipo: 'correcao',
				titulo: 'Recarregar a página não leva mais para o início',
				descricao:
					'Atualizar a tela, ou abrir um endereço direto pelo navegador, jogava você de volta ao dashboard. Agora você continua exatamente na página em que estava, o que também faz os links salvos nos favoritos funcionarem.'
			},
			{
				id: '2026-08-recuperacao-senha',
				tipo: 'correcao',
				titulo: 'Link de recuperação de senha funcionando',
				descricao:
					'Abrir o link recebido por e-mail, ou recarregar a tela de nova senha, derrubava a pessoa no login e o código enviado se perdia, obrigando a pedir outro. O link agora abre direto na tela certa.'
			},
			{
				id: '2026-08-telas-acesso',
				tipo: 'melhoria',
				titulo: 'Telas de acesso mais claras',
				descricao:
					'Login, esqueci minha senha e troca de senha ganharam o mesmo visual e passaram a conferir e-mail, senha e confirmação antes de enviar, avisando embaixo do campo o que precisa ser corrigido.'
			},
			{
				id: '2026-08-dados-por-perfil',
				tipo: 'seguranca',
				titulo: 'Cada perfil enxerga apenas os próprios dados',
				descricao:
					'O sistema passou a conferir, a cada consulta, se o aluno, a aula ou o book pertence a quem está pedindo. Um professor vê os seus alunos e as suas aulas, e um aluno vê apenas o material liberado para ele.',
				publico: [ 'ADMIN' ]
			},
			{
				id: '2026-08-sessao-30-dias',
				tipo: 'melhoria',
				titulo: 'Menos pedidos de login',
				descricao:
					'A sessão passou a durar 30 dias, então você continua conectado entre um dia de aula e outro sem precisar entrar de novo a cada visita.'
			},
			{
				id: '2026-08-cadastro-aula',
				tipo: 'correcao',
				titulo: 'Cadastro de aula corrigido',
				descricao:
					'Agendar uma aula nova podia falhar mesmo com todos os campos preenchidos corretamente. O agendamento voltou a funcionar.'
			}
		]
	}
];
